/**
 * Sales' Home as a dashboard (Lj, Oct 16: "multiple cards, polished like the Field dashboard … line chart cards I can
 * click on and they expand"). One shape for the web and, later, the phone. Every number comes from what is on the
 * device: the books' Sales feed (booksfeed.js — leads, bids sent / won / lost, the open pipeline), the matched call
 * log (calls.js), tasks tagged #assessment, and the ops projects. Each card carries `counted` (what it counted) and
 * `leftOut` (what it didn't), the Field cards' habit. Medians, never averages: one six-week lead wrecks an average at
 * this volume. Pure — no I/O.
 *
 * Until the lead-is-job remodel lands (a lead = a books job in a lead stage) the feed counts a lead once per PERSON;
 * every card here says so in its leftOut. The shape doesn't change when it lands — the rows just become jobs.
 */
import { goalsFor } from './deptgoals.js';
import { canonTag } from './tags.js';
import { contractValueOf } from './profit.js';
import { addDaysStr } from './phonestats.js';
import { localDateStr } from './time.js';

const DAY = 86400000;
const num = (v) => (v == null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
const isDay = (v) => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v);
const dayMs = (d) => new Date(`${d}T12:00:00`).getTime();
const daysBetween = (a, b) => (isDay(a) && isDay(b) ? Math.round((dayMs(b) - dayMs(a)) / DAY) : null);
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const money = (v) => `${v < 0 ? '−' : ''}$${Math.abs(Math.round(Number(v) || 0)).toLocaleString('en-CA')}`;
const LIVE = (p) => p && !p.deletedAt && !p.archived && !['archived', 'completed', 'cancelled'].includes(p.status);

/** The Monday of the week holding `date`. */
export function mondayOf(date) {
  const d = new Date(`${date}T12:00:00`);
  return addDaysStr(date, -((d.getDay() + 6) % 7));
}

/** The middle value (the mean of the two middle values for an even count), one decimal. null for nothing. */
export function median(xs) {
  const a = (xs || []).filter((x) => Number.isFinite(x)).sort((p, q) => p - q);
  if (!a.length) return null;
  const m = Math.floor(a.length / 2);
  return Math.round((a.length % 2 ? a[m] : (a[m - 1] + a[m]) / 2) * 10) / 10;
}

/** Defaults Lj hasn't overruled (plan note, Oct 16): follow up after 3 days quiet; a bid out within 2 days of the visit. */
export const SALES_DEFAULTS = Object.freeze({ followUpDays: 3, turnaroundDays: 2, contactHours: 24, staleDays: 14, weekHours: 40, weeks: 8, winRate: 40 });

/** The weeks on the charts: `n` of them, oldest first, each { from (Monday), to (Sunday or today), label }. */
export function salesWeeks(today, n = SALES_DEFAULTS.weeks) {
  const thisMon = mondayOf(today);
  return Array.from({ length: n }, (_, i) => {
    const from = addDaysStr(thisMon, -7 * (n - 1 - i));
    const to = addDaysStr(from, 6);
    return { from, to: to > today ? today : to, label: from };
  });
}
const byWeekMedian = (weeks, items, dateOf, valueOf) => weeks.map((w) => {
  const xs = items.filter((it) => { const d = dateOf(it); return d && d >= w.from && d <= w.to; }).map(valueOf);
  return { from: w.from, value: median(xs), n: xs.length };
});
const byWeekSum = (weeks, items, dateOf, valueOf = () => 1) => weeks.map((w) => {
  const rows = items.filter((it) => { const d = dateOf(it); return d && d >= w.from && d <= w.to; });
  return { from: w.from, value: rows.reduce((a, it) => a + (num(valueOf(it)) || 0), 0), n: rows.length };
});

/**
 * Everything the Sales Home draws. opts override SALES_DEFAULTS.
 * → { ready, today, from, weeks, pipeline, sold, winRate, cycle, turnaround, followUps, sources, lost, backlog, targets }
 *   ready = false when the books' Sales feed hasn't reached the device (every card then shows its waiting state).
 */
export function salesDashboard(dept, data = {}, opts = {}) {
  const o = { ...SALES_DEFAULTS, ...opts };
  const now = data.now || Date.now();
  const today = localDateStr(now);
  const f = data.sales?.ready ? data.sales : null;
  const weeks = salesWeeks(today, o.weeks);
  const goals = dept ? goalsFor(dept, data) : [];
  const goalTarget = (key) => { const g = goals.find((x) => x.key === key); return g && num(g.target) != null ? { value: num(g.target), period: g.period || 'month', from: 'goal' } : null; };
  const targets = {
    sold: goalTarget('sales:wonValue'),
    winRate: goalTarget('sales:winRate') || { value: o.winRate, period: 'month', from: 'preset' },
    followUpDays: o.followUpDays, turnaroundDays: o.turnaroundDays, contactHours: o.contactHours, staleDays: o.staleDays, weekHours: o.weekHours,
  };
  if (!f) {
    const wait = { ready: false, counted: null, leftOut: ['the books’ Sales feed hasn’t reached this device yet'] };
    return { ready: false, today, from: null, weeks, targets, pipeline: wait, sold: wait, winRate: wait, cycle: wait, turnaround: wait, followUps: wait, sources: wait, lost: wait, backlog: backlog(data, [], o, today) };
  }
  const personNote = 'a lead is counted once per person until the lead-is-job remodel lands (Susane’s three jobs = one lead)';
  const spanNote = `the books’ feed starts ${f.from}; nothing earlier is counted`;

  // ── the rows, indexed ──
  const leads = f.leads.filter((r) => isDay(r.date));
  const sent = f.sent.filter((r) => isDay(r.date));
  const won = f.won.filter((r) => isDay(r.date));
  const lost = f.lost.filter((r) => isDay(r.date));
  const sentById = new Map(sent.map((r) => [r.id, r]));
  const decidedIds = new Set([...won, ...lost].map((r) => r.id));
  const firstLead = new Map();   // customerId → earliest lead date
  for (const r of leads) if (r.customerId && (!firstLead.has(r.customerId) || r.date < firstLead.get(r.customerId))) firstLead.set(r.customerId, r.date);
  const sourceOf = new Map();    // customerId → source (the lead row's, first seen)
  for (const r of leads) if (r.customerId && r.source && !sourceOf.has(r.customerId)) sourceOf.set(r.customerId, r.source);
  const sentByCustomer = new Map();
  for (const r of sent) if (r.customerId) sentByCustomer.set(r.customerId, [...(sentByCustomer.get(r.customerId) || []), r]);

  // calls (matched, on the device) and tasks tagged #assessment, by customer
  const calls = (data.calls || []).filter((c) => c && !c.deletedAt && c.customerId && c.kind !== 'other' && isDay(c.dateLocal || (c.startedAt ? localDateStr(c.startedAt) : null)));
  const callDate = (c) => c.dateLocal || localDateStr(c.startedAt);
  const callsByCustomer = new Map();
  for (const c of calls) callsByCustomer.set(c.customerId, [...(callsByCustomer.get(c.customerId) || []), callDate(c)]);
  const projects = (data.projects || []).filter((p) => p && !p.deletedAt);
  const projById = new Map(projects.map((p) => [p.id, p]));
  const customerOfProject = (id) => projById.get(id)?.customerId || null;
  const tasks = (data.tasks || []).filter((t) => t && !t.deletedAt);
  const hasTag = (t, tag) => Array.isArray(t?.tags) && t.tags.some((x) => canonTag(String(x)) === tag);
  const assessments = tasks.filter((t) => t.status === 'done' && t.completedAt && hasTag(t, 'assessment') && customerOfProject(t.projectId));
  const assessByCustomer = new Map();
  for (const t of assessments) { const c = customerOfProject(t.projectId); assessByCustomer.set(c, [...(assessByCustomer.get(c) || []), localDateStr(t.completedAt)]); }
  const doneByCustomer = new Map();   // last task finished on the customer's projects — a touch
  for (const t of tasks) {
    if (t.status !== 'done' || !t.completedAt) continue;
    const c = customerOfProject(t.projectId); if (!c) continue;
    const d = localDateStr(t.completedAt);
    if (!doneByCustomer.has(c) || d > doneByCustomer.get(c)) doneByCustomer.set(c, d);
  }
  const firstAfter = (dates, from) => (dates || []).filter((d) => d >= from).sort()[0] || null;
  const lastOf = (dates) => (dates || []).slice().sort().slice(-1)[0] || null;

  // ── 1 · Pipeline $ ──
  const openBids = sent.filter((r) => !decidedIds.has(r.id)).map((r) => {
    const lastCall = lastOf(callsByCustomer.get(r.customerId));
    const lastDone = doneByCustomer.get(r.customerId) || null;
    const lastTouch = [r.date, lastCall, lastDone].filter(Boolean).sort().slice(-1)[0];
    const quiet = daysBetween(lastTouch, today);
    return { id: r.id, number: r.number || null, customer: r.customer, customerId: r.customerId || null, value: num(r.value) || 0, sentDate: r.date, ageDays: daysBetween(r.date, today), lastTouch, quietDays: quiet, overdue: quiet > o.followUpDays, stale: daysBetween(r.date, today) > o.staleDays };
  }).sort((a, b) => b.value - a.value);
  const pipeline = {
    ready: true, value: num(f.pipeline?.value) || 0, count: num(f.pipeline?.count) || 0,
    stages: (f.pipeline?.stages || []).map((s) => ({ stage: s.stage, count: num(s.count) || 0, value: num(s.value) || 0 })),
    sentOpen: { value: openBids.reduce((a, r) => a + r.value, 0), count: openBids.length },
    byWeek: weeks.map((w) => {
      const rows = sent.filter((r) => r.date <= w.to && !([...won, ...lost].some((d) => d.id === r.id && d.date <= w.to)));
      return { from: w.from, value: rows.reduce((a, r) => a + (num(r.value) || 0), 0), n: rows.length };
    }),
    rows: openBids,
    counted: `${plural(f.pipeline?.count || 0, 'lead')} still in play, ${money(f.pipeline?.value || 0)} of open bids (the books’ pipeline) · the line: bids sent and not yet decided, at each week’s end`,
    leftOut: ['change orders', 'bids still in draft aren’t on the line (they are in the books’ open pipeline)', spanNote],
  };

  // ── 2 · Sold $ ──
  const month = today.slice(0, 7);
  const wonMonth = won.filter((r) => r.date.slice(0, 7) === month);
  const soldTarget = targets.sold ? (targets.sold.period === 'month' ? targets.sold.value : targets.sold.period === 'week' ? targets.sold.value * 4.33 : null) : null;
  const sold = {
    ready: true,
    month: { value: wonMonth.reduce((a, r) => a + (num(r.value) || 0), 0), count: wonMonth.length },
    span: { value: won.reduce((a, r) => a + (num(r.value) || 0), 0), count: won.length, from: f.from },
    target: soldTarget, weeklyTarget: soldTarget ? Math.round(soldTarget / 4.33) : null, targetFrom: targets.sold?.from || null,
    byWeek: byWeekSum(weeks, won, (r) => r.date, (r) => r.value),
    rows: won.map((r) => ({ id: r.id, number: r.number || null, customer: r.customer, customerId: r.customerId || null, value: num(r.value) || 0, date: r.date, days: num(r.days), deposit: r.deposit || null, internalStatus: r.internalStatus || null })).sort((a, b) => b.date.localeCompare(a.date)),
    counted: `${plural(wonMonth.length, 'bid')} signed this month, ${money(wonMonth.reduce((a, r) => a + (num(r.value) || 0), 0))} before tax · by the day they were signed`,
    leftOut: ['change orders', 'deposits — sold is the signature, not the money', spanNote],
  };

  // ── 3 · Win rate (rolling 90 days) ──
  const decided = [...won.map((r) => ({ ...r, outcome: 'won' })), ...lost.map((r) => ({ ...r, outcome: 'lost' }))].sort((a, b) => b.date.localeCompare(a.date));
  const rate = (to) => {
    const from = addDaysStr(to, -89);
    const rows = decided.filter((r) => r.date >= from && r.date <= to);
    const w = rows.filter((r) => r.outcome === 'won').length;
    return { value: rows.length ? Math.round((w / rows.length) * 100) : null, won: w, lost: rows.length - w, n: rows.length };
  };
  const r90 = rate(today);
  const winRate = {
    ready: true, value: r90.value, won: r90.won, lost: r90.lost, decided: r90.n, target: targets.winRate.value, targetFrom: targets.winRate.from,
    byWeek: weeks.map((w) => ({ from: w.from, ...rate(w.to) })),
    rows: decided.map((r) => ({ id: r.id, number: r.number || null, customer: r.customer, customerId: r.customerId || null, value: num(r.value) || 0, date: r.date, outcome: r.outcome, days: num(r.days) })),
    counted: `${plural(r90.won, 'bid')} won of ${r90.n} decided in 90 days (won + declined / expired) · the line: the same 90 days ending each week`,
    leftOut: ['bids still open — they aren’t decided', 'change orders', spanNote],
  };

  // ── 4 · Cycle times (per customer, by the end event's date; medians) ──
  const items = { contact: [], assessment: [], sent: [], decision: [], leadToWon: [] };
  for (const [cid, leadDate] of firstLead) {
    const contact = firstAfter(callsByCustomer.get(cid), leadDate);
    if (contact) items.contact.push({ customerId: cid, date: contact, days: daysBetween(leadDate, contact) });
    const assess = firstAfter(assessByCustomer.get(cid), leadDate);
    if (assess) items.assessment.push({ customerId: cid, date: assess, days: daysBetween(leadDate, assess) });
    const firstSent = firstAfter((sentByCustomer.get(cid) || []).map((r) => r.date), assess || leadDate);
    if (assess && firstSent) items.sent.push({ customerId: cid, date: firstSent, days: daysBetween(assess, firstSent), customer: sentByCustomer.get(cid)[0].customer });
    const w = won.filter((r) => r.customerId === cid && r.date >= leadDate).sort((a, b) => a.date.localeCompare(b.date))[0];
    if (w) items.leadToWon.push({ customerId: cid, date: w.date, days: daysBetween(leadDate, w.date) });
  }
  for (const r of decided) {
    const s = sentById.get(r.id);
    const days = r.outcome === 'won' && num(r.days) != null ? Math.round(num(r.days)) : s ? daysBetween(s.date, r.date) : null;
    if (days != null) items.decision.push({ customerId: r.customerId, date: r.date, days, outcome: r.outcome });
  }
  const stageDef = [
    { key: 'contact', label: 'Lead → first call', target: o.contactHours / 24, unit: 'd' },
    { key: 'assessment', label: 'Lead → assessment', target: null, unit: 'd' },
    { key: 'sent', label: 'Assessment → bid sent', target: o.turnaroundDays, unit: 'd' },
    { key: 'decision', label: 'Bid sent → decision', target: null, unit: 'd' },
  ];
  const stages = stageDef.map((s) => ({ ...s, median: median(items[s.key].map((x) => x.days)), n: items[s.key].length, byWeek: byWeekMedian(weeks, items[s.key], (x) => x.date, (x) => x.days) }));
  const slowest = stages.filter((s) => s.median != null).sort((a, b) => b.median - a.median)[0] || null;
  const cycle = {
    ready: true, stages, slowest: slowest?.key || null,
    leadToWon: { median: median(items.leadToWon.map((x) => x.days)), n: items.leadToWon.length, byWeek: byWeekMedian(weeks, items.leadToWon, (x) => x.date, (x) => x.days) },
    counted: 'days per customer, from the lead’s first date to each later event — the median of the customers whose event landed that week',
    leftOut: ['first call = the first matched phone call after the lead (texts and emails aren’t seen)', 'assessment = a task tagged #assessment finished on that customer’s job', personNote],
  };

  // ── 5 · Estimate turnaround (assessment → bid sent), with the visits still waiting for a bid ──
  const waiting = [];
  for (const [cid, dates] of assessByCustomer) {
    const last = lastOf(dates);
    if (!last || firstAfter((sentByCustomer.get(cid) || []).map((r) => r.date), last)) continue;
    const name = leads.find((r) => r.customerId === cid)?.name || projects.find((p) => p.customerId === cid)?.customerName || projects.find((p) => p.customerId === cid)?.name || 'Customer';
    const projectId = projects.find((p) => p.customerId === cid)?.id || null;
    waiting.push({ customerId: cid, customer: name, projectId, assessmentDate: last, days: daysBetween(last, today), late: daysBetween(last, today) > o.turnaroundDays });
  }
  waiting.sort((a, b) => b.days - a.days);
  const turnaround = {
    ready: true, median: stages[2].median, n: stages[2].n, target: o.turnaroundDays, byWeek: stages[2].byWeek,
    rows: items.sent.map((x) => ({ customerId: x.customerId, customer: x.customer, date: x.date, days: x.days, late: x.days > o.turnaroundDays })).sort((a, b) => b.date.localeCompare(a.date)),
    waiting, worst: waiting[0] || null,
    counted: `${plural(stages[2].n, 'bid')} sent after a finished #assessment task on the same customer · days from the visit to the send`,
    leftOut: ['bids sent with no #assessment task before them (phone quotes) aren’t timed', personNote],
  };

  // ── 6 · Follow-ups ──
  const days14 = Array.from({ length: 14 }, (_, i) => addDaysStr(today, -(13 - i)));
  const openCustomers = new Set(openBids.map((r) => r.customerId).filter(Boolean));
  const touchDates = [
    ...calls.filter((c) => openCustomers.has(c.customerId)).map(callDate),
    ...tasks.filter((t) => t.status === 'done' && t.completedAt && openCustomers.has(customerOfProject(t.projectId))).map((t) => localDateStr(t.completedAt)),
  ];
  const weekFrom = mondayOf(today);
  const followUps = {
    ready: true, overdue: openBids.filter((r) => r.overdue).length, open: openBids.length, touchedWeek: touchDates.filter((d) => d >= weekFrom).length, target: o.followUpDays,
    perDay: days14.map((d) => ({ date: d, value: touchDates.filter((x) => x === d).length })),
    rows: openBids.slice().sort((a, b) => (b.quietDays ?? 0) - (a.quietDays ?? 0)),
    counted: `${plural(openBids.length, 'open bid')}; a touch = a matched call to them or a task finished on their job · quiet > ${o.followUpDays} d = overdue`,
    leftOut: ['emails and texts aren’t seen as touches', 'leads with no bid out yet (they’re in Cycle times)', personNote],
  };

  // ── 7 · Lead source ──
  const srcOf = (r) => r.source || sourceOf.get(r.customerId) || 'unknown';
  const bySrc = new Map();
  for (const r of leads) { const k = srcOf(r); const s = bySrc.get(k) || { source: k, leads: 0, won: 0, value: 0 }; s.leads++; bySrc.set(k, s); }
  for (const r of won) { const k = srcOf(r); const s = bySrc.get(k) || { source: k, leads: 0, won: 0, value: 0 }; s.won++; s.value += num(r.value) || 0; bySrc.set(k, s); }
  const srcRows = [...bySrc.values()].map((s) => ({ ...s, winPct: s.leads ? Math.round((s.won / s.leads) * 100) : null })).sort((a, b) => b.leads - a.leads || b.won - a.won);
  const topSrc = srcRows.filter((s) => s.source !== 'unknown').slice(0, 3).map((s) => s.source);
  const sources = {
    ready: true, rows: srcRows, top: srcRows[0] || null, series: [...topSrc, ...(srcRows.length > topSrc.length ? ['other'] : [])],
    byWeek: weeks.map((w) => {
      const rows = leads.filter((r) => r.date >= w.from && r.date <= w.to);
      const counts = {};
      for (const r of rows) { const k = topSrc.includes(srcOf(r)) ? srcOf(r) : 'other'; counts[k] = (counts[k] || 0) + 1; }
      return { from: w.from, counts, n: rows.length };
    }),
    counted: `${plural(leads.length, 'lead')} since ${f.from} by where they came from; won = that person’s bids signed`,
    leftOut: ['a lead with no source reads “unknown” — set it in BattleBids', personNote],
  };

  // ── 8 · Why we lose ──
  const lost90 = lost.filter((r) => r.date >= addDaysStr(today, -89));
  const reasons = new Map();
  for (const r of lost90) if (r.reason) reasons.set(r.reason, (reasons.get(r.reason) || 0) + 1);
  const lostCard = {
    ready: true, n90: lost90.length, value90: lost90.reduce((a, r) => a + (num(r.value) || 0), 0), reasonsTracked: reasons.size > 0,
    reasons: [...reasons.entries()].map(([reason, n]) => ({ reason, n })).sort((a, b) => b.n - a.n), top: [...reasons.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] || null,
    byWeek: byWeekSum(weeks, lost, (r) => r.date),
    rows: lost.map((r) => ({ id: r.id, number: r.number || null, customer: r.customer, customerId: r.customerId || null, value: num(r.value) || 0, date: r.date, reason: r.reason || null, days: sentById.get(r.id) ? daysBetween(sentById.get(r.id).date, r.date) : null })).sort((a, b) => b.date.localeCompare(a.date)),
    counted: `${plural(lost90.length, 'bid')} declined or expired in 90 days, ${money(lost90.reduce((a, r) => a + (num(r.value) || 0), 0))}`,
    leftOut: [reasons.size ? 'bids lost before reasons were tracked read “no reason”' : 'the reason isn’t tracked yet — it comes with the loss-reason field in BattleBids', spanNote],
  };

  return { ready: true, today, from: f.from, weeks, targets, pipeline, sold, winRate, cycle, turnaround, followUps, sources, lost: lostCard, backlog: backlog(data, won, o, today) };
}

/** Sold, not started: live ops jobs with a contract value (or a signed bid) and no time logged yet. Crew-weeks at `weekHours`. */
function backlog(data, won, o, today) {
  const projects = (data.projects || []).filter((p) => LIVE(p) && !p.internal);
  const tasks = (data.tasks || []).filter((t) => t && !t.deletedAt);
  const jobs = new Map((data.jobs || []).filter(Boolean).map((j) => [j.projectId || j.id, j]));
  const worked = new Set();
  const estByProject = new Map();
  for (const t of tasks) {
    if (!t.projectId) continue;
    if ((t.timeEntries || []).some((e) => e && (e.duration || 0) > 0) || (t.timeSpent || 0) > 0) worked.add(t.projectId);
    if (t.status !== 'done' && num(t.estimatedMinutes)) estByProject.set(t.projectId, (estByProject.get(t.projectId) || 0) + num(t.estimatedMinutes));
  }
  const wonByCustomer = new Map();
  for (const r of won) if (r.customerId) wonByCustomer.set(r.customerId, (wonByCustomer.get(r.customerId) || 0) + (num(r.value) || 0));
  const rows = projects.filter((p) => !worked.has(p.id)).map((p) => {
    const value = contractValueOf(p, jobs.get(p.bookProjectId) || null) ?? (p.customerId ? wonByCustomer.get(p.customerId) : null) ?? null;
    if (!(value > 0)) return null;
    const hours = estByProject.has(p.id) ? Math.round(estByProject.get(p.id) / 6) / 10 : null;
    return { projectId: p.id, name: p.name, customerId: p.customerId || null, value, hours, startDate: p.startDate || null, createdAt: num(p.createdAt) };
  }).filter(Boolean).sort((a, b) => b.value - a.value);
  const hours = rows.reduce((a, r) => a + (r.hours || 0), 0);
  const estimated = rows.filter((r) => r.hours != null).length;
  return {
    ready: true, value: rows.reduce((a, r) => a + r.value, 0), count: rows.length, hours, weeks: hours ? Math.round((hours / o.weekHours) * 10) / 10 : null, weekHours: o.weekHours, rows,
    counted: `${plural(rows.length, 'job')} sold and not started (a contract value, no time logged) · hours = open tasks’ estimates, ${estimated} of ${rows.length} jobs have them`,
    leftOut: ['jobs with time already logged — they’re under way, Field’s side', `crew-weeks assume ${o.weekHours} h a week, one person`, `as of ${today}`],
  };
}
